import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { invoicesApi } from '../../api/endpoints';
import { InvoiceDetail, InvoiceTypeMap, InvoiceStatusMap } from '../../types';
import {
  FiSend,
  FiDownload,
  FiTrash2,
  FiPlus,
  FiX,
  FiEye,
  FiArrowLeft,
  FiFileText,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [creditNoteData, setCreditNoteData] = useState({
    creditNoteNumber: '',
    creditNoteDate: new Date().toISOString().split('T')[0],
    reason: '',
    items: [] as { amazonProductId: string; description: string; quantity: number; unitPrice: number; taxRate: number; hsCode: string }[],
  });
  const [creatingCn, setCreatingCn] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const fetchInvoice = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await invoicesApi.get(id);
      setInvoice(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd pobierania faktury');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleSubmitToAmazon = useCallback(async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await invoicesApi.submit(id);
      toast.success('Faktura wysłana do Amazon');
      fetchInvoice();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd wysyłania faktury');
    } finally {
      setSubmitting(false);
    }
  }, [id, fetchInvoice]);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await invoicesApi.delete(id);
      toast.success('Faktura usunięta');
      navigate('/invoices');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd usuwania faktury');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [id, navigate]);

  const handleDownloadPdf = useCallback(async () => {
    if (!id) return;
    try {
      const response = await invoicesApi.getPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `faktura-${invoice?.invoiceNumber || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd pobierania PDF');
    }
  }, [id, invoice]);

  const handlePreviewPdf = useCallback(async () => {
    if (!id) return;
    try {
      const response = await invoicesApi.getPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setShowPdfPreview(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd podglądu PDF');
    }
  }, [id]);

  const [generatingPdf, setGeneratingPdf] = useState(false);

  const handleGeneratePdf = useCallback(async () => {
    if (!id) return;
    setGeneratingPdf(true);
    try {
      const response = await invoicesApi.generatePdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setShowPdfPreview(true);
      toast.success('PDF wygenerowany');
      fetchInvoice(); // refresh hasPdf
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd generowania PDF');
    } finally {
      setGeneratingPdf(false);
    }
  }, [id, fetchInvoice]);

  const closePdfPreview = useCallback(() => {
    setShowPdfPreview(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }, [pdfUrl]);

  // Credit note
  const openCreditNoteModal = useCallback(() => {
    if (!invoice) return;
    setCreditNoteData({
      creditNoteNumber: `CN/${invoice.invoiceNumber}`,
      creditNoteDate: new Date().toISOString().split('T')[0],
      reason: '',
      items: invoice.items.map((i) => ({
        amazonProductId: i.amazonProductId,
        description: i.description,
        quantity: i.quantity,
        unitPrice: i.unitPrice,
        taxRate: i.taxRate,
        hsCode: i.hsCode || '',
      })),
    });
    setShowCreditNoteModal(true);
  }, [invoice]);

  const handleCreateCreditNote = useCallback(async () => {
    if (!id) return;
    setCreatingCn(true);
    try {
      const response = await invoicesApi.createCreditNote(id, creditNoteData);
      toast.success('Nota kredytowa utworzona');
      setShowCreditNoteModal(false);
      navigate(`/invoices/${response.data.id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd tworzenia noty kredytowej');
    } finally {
      setCreatingCn(false);
    }
  }, [id, creditNoteData, navigate]);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('pl-PL', { style: 'currency', currency: invoice?.currency || 'EUR' });

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">Nie znaleziono faktury.</div>
        <Link to="/invoices" className="btn btn-secondary">
          Powrót do listy
        </Link>
      </div>
    );
  }

  const isDraft = invoice.status === 0;
  const typeName = InvoiceTypeMap[invoice.invoiceType] || 'Faktura';
  const statusName = InvoiceStatusMap[invoice.status] || 'Nieznany';

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <Link to="/invoices" className="btn btn-link">
            <FiArrowLeft /> Faktury
          </Link>
          <h1>
            {typeName} {invoice.invoiceNumber}
          </h1>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {isDraft && (
            <>
              <button
                className="btn btn-primary"
                onClick={() => setShowSubmitConfirm(true)}
                disabled={submitting}
              >
                <FiSend /> {submitting ? 'Wysyłanie...' : 'Wyślij do Amazon'}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
              >
                <FiTrash2 /> {deleting ? 'Usuwanie...' : 'Usuń'}
              </button>
            </>
          )}
          {invoice.invoiceType === 0 && invoice.status !== 0 && (
            <button className="btn btn-secondary" onClick={openCreditNoteModal}>
              <FiPlus /> Nota kredytowa
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={handleGeneratePdf}
            disabled={generatingPdf}
          >
            <FiFileText /> {generatingPdf ? 'Generuję...' : (invoice.hasPdf ? 'Regeneruj PDF' : 'Generuj PDF')}
          </button>
          {invoice.hasPdf && (
            <>
              <button className="btn btn-secondary" onClick={handlePreviewPdf}>
                <FiEye /> Podgląd PDF
              </button>
              <button className="btn btn-secondary" onClick={handleDownloadPdf}>
                <FiDownload /> Pobierz PDF
              </button>
            </>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-item">
          <span className="detail-label">Numer faktury</span>
          <span className="detail-value">{invoice.invoiceNumber}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Typ</span>
          <span className="detail-value">
            <span className={`badge ${invoice.invoiceType === 1 ? 'badge-cancelled' : invoice.invoiceType === 2 ? 'badge-pending' : 'badge-active'}`}>
              {typeName}
            </span>
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Zamówienie PO</span>
          <span className="detail-value">
            <Link to={`/purchase-orders/${invoice.purchaseOrderId}`}>
              {invoice.purchaseOrderNumber}
            </Link>
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Status</span>
          <span className="detail-value">
            <span className={`badge badge-${invoice.status === 0 ? 'draft' : invoice.status === 1 ? 'pending' : invoice.status === 2 ? 'confirmed' : invoice.status === 4 ? 'delivered' : invoice.status === 3 ? 'cancelled' : 'draft'}`}>
              {statusName}
            </span>
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">ID faktury Amazon</span>
          <span className="detail-value">{invoice.amazonInvoiceId || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">ID transakcji Amazon</span>
          <span className="detail-value">{invoice.amazonTransactionId || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Data faktury</span>
          <span className="detail-value">{formatDate(invoice.invoiceDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Termin płatności</span>
          <span className="detail-value">{formatDate(invoice.dueDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Waluta</span>
          <span className="detail-value">{invoice.currency}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Warunki płatności</span>
          <span className="detail-value">{invoice.paymentTerms || '-'}</span>
        </div>
        {invoice.notes && (
          <div className="detail-item" style={{ gridColumn: 'span 2' }}>
            <span className="detail-label">Uwagi</span>
            <span className="detail-value">{invoice.notes}</span>
          </div>
        )}
        {invoice.originalInvoiceId && (
          <div className="detail-item">
            <span className="detail-label">Faktura pierwotna</span>
            <span className="detail-value">
              <Link to={`/invoices/${invoice.originalInvoiceId}`}>
                Zobacz fakturę
              </Link>
            </span>
          </div>
        )}
        <div className="detail-item">
          <span className="detail-label">Wysłano</span>
          <span className="detail-value">{formatDate(invoice.submittedAt)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Utworzono</span>
          <span className="detail-value">{formatDate(invoice.createdAt)}</span>
        </div>
      </div>

      {/* Items */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <h3>Pozycje faktury</h3>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Opis</th>
                  <th>ID produktu</th>
                  <th>ASIN</th>
                  <th style={{ textAlign: 'right' }}>Ilość</th>
                  <th style={{ textAlign: 'right' }}>Cena jedn.</th>
                  <th style={{ textAlign: 'right' }}>Wartość</th>
                  <th style={{ textAlign: 'right' }}>Stawka VAT</th>
                  <th style={{ textAlign: 'right' }}>Kwota VAT</th>
                  <th>Kod HS</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.itemSequenceNumber}</td>
                    <td>{item.description}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{item.amazonProductId}</td>
                    <td>{item.asin || '-'}</td>
                    <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.unitPrice)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.totalPrice)}</td>
                    <td style={{ textAlign: 'right' }}>{item.taxRate}%</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.taxAmount)}</td>
                    <td>{item.hsCode || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="card-body">
          <div className="totals-grid">
            <div className="total-row">
              <span className="total-label">Wartość netto:</span>
              <span className="total-value">{formatCurrency(invoice.subTotal)}</span>
            </div>
            <div className="total-row">
              <span className="total-label">Kwota VAT:</span>
              <span className="total-value">{formatCurrency(invoice.taxAmount)}</span>
            </div>
            <div className="total-row total-row-final">
              <span className="total-label">Razem brutto:</span>
              <span className="total-value">{formatCurrency(invoice.totalAmount)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Credit notes */}
      {invoice.creditNotes && invoice.creditNotes.length > 0 && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <div className="card-header">
            <h3>Noty kredytowe</h3>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Numer</th>
                    <th>Status</th>
                    <th>Data</th>
                    <th style={{ textAlign: 'right' }}>Kwota</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.creditNotes.map((cn) => (
                    <tr key={cn.id}>
                      <td>{cn.invoiceNumber}</td>
                      <td><span className="badge badge-cancelled">{cn.status}</span></td>
                      <td>{formatDate(cn.invoiceDate)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(cn.totalAmount)}</td>
                      <td>
                        <Link to={`/invoices/${cn.id}`} className="btn btn-link btn-sm">
                          <FiFileText /> Szczegóły
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log */}
      <InvoiceAuditLogSection invoiceId={id || ''} />

      {/* PDF Preview Modal */}
      {showPdfPreview && pdfUrl && (
        <div className="modal-overlay" onClick={closePdfPreview}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', width: '900px', height: '85vh' }}
          >
            <div className="modal-header">
              <h2>Podgląd PDF - {invoice.invoiceNumber}</h2>
              <button className="btn btn-icon" onClick={closePdfPreview}>
                <FiX />
              </button>
            </div>
            <div className="modal-body" style={{ padding: 0, flex: 1, overflow: 'hidden' }}>
              <iframe
                src={pdfUrl}
                style={{ width: '100%', height: '100%', border: 'none', minHeight: '60vh' }}
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}

      {/* Credit Note Modal */}
      {showCreditNoteModal && (
        <div className="modal-overlay" onClick={() => setShowCreditNoteModal(false)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '700px', maxHeight: '80vh', overflow: 'auto' }}
          >
            <div className="modal-header">
              <h2>Nowa nota kredytowa</h2>
              <button className="btn btn-icon" onClick={() => setShowCreditNoteModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Numer noty *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={creditNoteData.creditNoteNumber}
                    onChange={(e) =>
                      setCreditNoteData((p) => ({ ...p, creditNoteNumber: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Data *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={creditNoteData.creditNoteDate}
                    onChange={(e) =>
                      setCreditNoteData((p) => ({ ...p, creditNoteDate: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Powód</label>
                <textarea
                  className="form-control"
                  value={creditNoteData.reason}
                  onChange={(e) =>
                    setCreditNoteData((p) => ({ ...p, reason: e.target.value }))
                  }
                  rows={2}
                  placeholder="Powód wystawienia noty..."
                />
              </div>
              <h4 style={{ marginTop: '1rem' }}>Pozycje (możesz zmienić ilości)</h4>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Opis</th>
                      <th>Ilość</th>
                      <th>Cena jedn.</th>
                      <th>Wartość</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditNoteData.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.description}</td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={item.quantity}
                            min={0}
                            style={{ width: '80px' }}
                            onChange={(e) => {
                              const newItems = [...creditNoteData.items];
                              newItems[idx] = { ...newItems[idx], quantity: Number(e.target.value) };
                              setCreditNoteData((p) => ({ ...p, items: newItems }));
                            }}
                          />
                        </td>
                        <td>{formatCurrency(item.unitPrice)}</td>
                        <td>{formatCurrency(item.quantity * item.unitPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreditNoteModal(false)}>
                Anuluj
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateCreditNote}
                disabled={creatingCn || !creditNoteData.creditNoteNumber}
              >
                {creatingCn ? 'Tworzenie...' : 'Utwórz notę kredytową'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Usunięcie faktury"
        message={`Czy na pewno chcesz usunąć fakturę ${invoice?.invoiceNumber}? Ta operacja jest nieodwracalna.`}
        confirmLabel="Usuń fakturę"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        loading={deleting}
      />

      {/* Submit confirmation */}
      <ConfirmDialog
        isOpen={showSubmitConfirm}
        title="Wysyłanie faktury do Amazon"
        message={`Czy wysłać fakturę ${invoice?.invoiceNumber} (${invoice?.items.length} pozycji, ${invoice ? invoice.totalAmount.toLocaleString('pl-PL', { style: 'currency', currency: invoice.currency }) : ''}) do Amazon? Po wysłaniu nie będzie można jej edytować.`}
        confirmLabel="Wyślij do Amazon"
        variant="warning"
        onConfirm={() => { setShowSubmitConfirm(false); handleSubmitToAmazon(); }}
        onCancel={() => setShowSubmitConfirm(false)}
        loading={submitting}
      />
    </div>
  );
};

// Audit Log Sub-component
const InvoiceAuditLogSection: React.FC<{ invoiceId: string }> = ({ invoiceId }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!invoiceId) return;
    const fetch = async () => {
      try {
        const res = await invoicesApi.getAuditLog(invoiceId);
        setLogs(res.data);
      } catch {
        // no audit logs
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [invoiceId]);

  if (loading) return null;
  if (logs.length === 0) return null;

  const ACTION_LABELS: Record<string, string> = {
    Created: 'Utworzono',
    StatusChanged: 'Zmiana statusu',
    Submitted: 'Wysłano do Amazon',
    SubmitFailed: 'Błąd wysyłki',
    Edited: 'Edytowano',
    Deleted: 'Usunięto',
    PdfGenerated: 'PDF wygenerowany',
    CreditNoteCreated: 'Nota kredytowa',
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString('pl-PL') + ' ' + d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  };

  const visibleLogs = expanded ? logs : logs.slice(0, 5);

  return (
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <h3>Historia zmian ({logs.length})</h3>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {expanded ? 'Zwiń' : 'Rozwiń'}
        </span>
      </div>
      <div className="card-body" style={{ padding: '12px 16px' }}>
        <ul className="audit-timeline">
          {visibleLogs.map((log) => (
            <li key={log.id} className={`audit-item action-${log.action}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div className="audit-action">
                    {ACTION_LABELS[log.action] || log.action}
                  </div>
                  {log.details && <div className="audit-details">{log.details}</div>}
                  {(log.oldValue || log.newValue) && (
                    <div className="audit-values">
                      {log.oldValue && <span className="audit-old-value">{log.oldValue}</span>}
                      {log.oldValue && log.newValue && <span>→</span>}
                      {log.newValue && <span className="audit-new-value">{log.newValue}</span>}
                    </div>
                  )}
                  {log.userName && (
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      przez: {log.userName}
                    </div>
                  )}
                </div>
                <span className="audit-time">{formatTime(log.createdAt)}</span>
              </div>
            </li>
          ))}
        </ul>
        {!expanded && logs.length > 5 && (
          <button
            className="btn btn-link btn-sm"
            onClick={() => setExpanded(true)}
            style={{ marginTop: '8px' }}
          >
            Pokaż wszystkie ({logs.length})
          </button>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetailPage;
